import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import dayjs, { Dayjs } from 'dayjs';
import { Observable, finalize, of, shareReplay, tap } from 'rxjs';
import {
  AggregationType,
  AnalyticsMetricType,
  BucketedAverageConfiguration,
  DateGroupBy,
  DateGranularity,
  DateSourceType,
  EmptyBucketMode,
  JobResultAnalyticsDashboardConfiguration,
  ResultFieldFilter,
} from './job-result-analytics-dashboard-configuration';
import { JobDashboardRange } from '../job-dashboard/job-dashboard-configuration';
import { environment } from '../../../../../environments/environment';
import {
  getFromForRange as getRangeFrom,
  getToForRange as getRangeTo,
} from '../job-dashboard/job-dashboard-range.utils';

export interface FieldInfo {
  key: string;
  inferredType: string;
  occurrenceCount: number;
  sampleValues: string[];
}

export interface JobResultFieldsResponse {
  fields: FieldInfo[];
  sampleSize: number;
}

export interface AggregationResult {
  groupKey: string;
  aggregations: { [key: string]: number };
}

export interface JobResultAnalyticsResponse {
  results: AggregationResult[];
  totalJobCount: number;
  warnings: string[];
}

export interface BucketValue {
  key: string;
  value: number;
}

export interface JobResultBucketedAverageResponse {
  granularity: DateGranularity;
  total: number;
  bucketCount: number;
  nonEmptyBucketCount: number;
  averagePerBucket: number;
  emptyBucketMode: EmptyBucketMode;
  buckets: BucketValue[];
  warnings: string[];
}

export type JobResultAnalyticsDataResponse =
  JobResultAnalyticsResponse | JobResultBucketedAverageResponse;

interface JobResultAnalyticsAggregateRequest {
  dataConnectionIds?: string[];
  workerIds?: string[];
  statuses?: string[];
  from?: Dayjs;
  to?: Dayjs;
  aggregations: JobResultAnalyticsDashboardConfiguration['aggregations'];
  filters?: JobResultAnalyticsDashboardConfiguration['filters'];
  groupBy?: string;
  dateGroupBy?: DateGroupBy;
  limit?: number;
}

interface JobResultBucketedAverageRequest {
  dataConnectionIds?: string[];
  workerIds?: string[];
  statuses?: string[];
  from?: Dayjs;
  to?: Dayjs;
  timezone: string;
  bucket: BucketedAverageConfiguration['bucket'];
  dateSource: BucketedAverageConfiguration['dateSource'];
  value: BucketedAverageConfiguration['value'];
  filters?: ResultFieldFilter[];
  emptyBucketMode: EmptyBucketMode;
}

@Injectable({
  providedIn: 'root',
})
export class JobResultAnalyticsDashboardService {
  protected http = inject(HttpClient);
  private readonly fieldsCacheTtlMs = 60_000;
  private readonly fieldsResponseCache = new Map<
    string,
    { response: JobResultFieldsResponse; expiresAt: number }
  >();
  private readonly fieldsInFlightRequests = new Map<
    string,
    Observable<JobResultFieldsResponse>
  >();

  discoverFields(
    dataConnectionIds?: string[],
    forceRefresh = false,
  ): Observable<JobResultFieldsResponse> {
    const normalizedIds = [...(dataConnectionIds ?? [])].sort();
    const cacheKey = normalizedIds.join(',');

    const inFlightRequest = this.fieldsInFlightRequests.get(cacheKey);
    if (inFlightRequest) {
      return inFlightRequest;
    }

    if (!forceRefresh) {
      const cached = this.fieldsResponseCache.get(cacheKey);
      if (cached && cached.expiresAt > Date.now()) {
        return of(cached.response);
      }
    }

    const request$ = this.http
      .post<JobResultFieldsResponse>(
        `${environment.apiUrls.workflow}/api/v1/dashboard/jobs/results/fields`,
        {
          dataConnectionIds:
            normalizedIds.length > 0 ? normalizedIds : undefined,
        },
      )
      .pipe(
        tap((response) => {
          this.fieldsResponseCache.set(cacheKey, {
            response,
            expiresAt: Date.now() + this.fieldsCacheTtlMs,
          });
        }),
        finalize(() => {
          this.fieldsInFlightRequests.delete(cacheKey);
        }),
        shareReplay(1),
      );

    this.fieldsInFlightRequests.set(cacheKey, request$);
    return request$;
  }

  getData(
    config: JobResultAnalyticsDashboardConfiguration,
  ): Observable<JobResultAnalyticsDataResponse> {
    if (config.metricType === AnalyticsMetricType.BUCKETED_AVERAGE) {
      return this.getBucketedAverageData(config);
    }

    return this.getAggregateData(config);
  }

  private getAggregateData(
    config: JobResultAnalyticsDashboardConfiguration,
  ): Observable<JobResultAnalyticsResponse> {
    let from = config.from;
    let to = config.to;
    if (config.range !== JobDashboardRange.CUSTOM) {
      const now = dayjs();
      from = this.getFromForRange(config.range, now);
      to = this.getToForRange(config.range, now);
    }

    const normalizedDateGroupBy = this.normalizeDateGroupBy(config.dateGroupBy);
    const requestBody: JobResultAnalyticsAggregateRequest = {
      dataConnectionIds: config.dataConnectionIds,
      workerIds: config.workerIds,
      statuses: config.statuses,
      from,
      to,
      aggregations: config.aggregations,
      filters: config.filters,
      groupBy: normalizedDateGroupBy ? undefined : config.groupBy,
      dateGroupBy: normalizedDateGroupBy,
      limit: config.limit,
    };

    return this.http.post<JobResultAnalyticsResponse>(
      `${environment.apiUrls.workflow}/api/v1/dashboard/jobs/results/aggregate`,
      requestBody,
    );
  }

  private getBucketedAverageData(
    config: JobResultAnalyticsDashboardConfiguration,
  ): Observable<JobResultBucketedAverageResponse> {
    let from = config.from;
    let to = config.to;
    if (config.range !== JobDashboardRange.CUSTOM) {
      const now = dayjs();
      from = this.getFromForRange(config.range, now);
      to = this.getToForRange(config.range, now);
    }

    const bucketedAverage = this.normalizeBucketedAverage(
      config.bucketedAverage,
    );
    const requestBody: JobResultBucketedAverageRequest = {
      dataConnectionIds: config.dataConnectionIds,
      workerIds: config.workerIds,
      statuses: config.statuses,
      from,
      to,
      timezone: bucketedAverage.timezone || this.getBrowserTimezone(),
      bucket: bucketedAverage.bucket,
      dateSource: bucketedAverage.dateSource,
      value: bucketedAverage.value,
      filters: config.filters,
      emptyBucketMode: bucketedAverage.emptyBucketMode,
    };

    return this.http.post<JobResultBucketedAverageResponse>(
      `${environment.apiUrls.workflow}/api/v1/dashboard/jobs/results/bucketed-average`,
      requestBody,
    );
  }

  private normalizeDateGroupBy(
    dateGroupBy?: DateGroupBy,
  ): DateGroupBy | undefined {
    if (!dateGroupBy) {
      return undefined;
    }

    const normalizedTimezone = dateGroupBy.timezone?.trim();
    if (!normalizedTimezone) {
      return { granularity: dateGroupBy.granularity };
    }

    return {
      granularity: dateGroupBy.granularity,
      timezone: normalizedTimezone,
    };
  }

  private normalizeBucketedAverage(
    bucketedAverage?: BucketedAverageConfiguration,
  ): BucketedAverageConfiguration {
    const dateSourceType =
      bucketedAverage?.dateSource?.type || DateSourceType.JOB_LAST_UPDATED;
    const dateSourceFieldKey = bucketedAverage?.dateSource?.fieldKey?.trim();

    return {
      bucket: {
        granularity:
          bucketedAverage?.bucket?.granularity || DateGranularity.DAY,
      },
      timezone: bucketedAverage?.timezone?.trim(),
      dateSource: {
        type: dateSourceType,
        fieldKey:
          dateSourceType === DateSourceType.RESULT_FIELD
            ? dateSourceFieldKey
            : undefined,
      },
      value: {
        fieldKey: bucketedAverage?.value?.fieldKey?.trim() || '',
        aggregation: AggregationType.COUNT_DISTINCT,
      },
      emptyBucketMode:
        bucketedAverage?.emptyBucketMode || EmptyBucketMode.INCLUDE_AS_ZERO,
    };
  }

  private getBrowserTimezone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
      return 'UTC';
    }
  }

  getFromForRange(range: JobDashboardRange, referenceTime = dayjs()): Dayjs {
    return getRangeFrom(range, referenceTime);
  }

  getToForRange(range: JobDashboardRange, referenceTime = dayjs()): Dayjs {
    return getRangeTo(range, referenceTime);
  }
}
